import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {makeReactContext} from 'lib/makeReactContext';
import {ProcedureValidationContext} from 'procedures/design/validation/ProcedureValidationContext';
import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType,
} from 'procedures/design/validation/ProcedureValidationTypes';
import MethodSelectorSidebar from 'procedures/planning/MethodSelectorSidebar';
import {
  ExperimentalInputsComponentName,
  ExperimentalInputsContextProperties,
} from 'procedures/planning/ProcedurePlanTypes';
import {useInputTable} from 'procedures/planning/useInputTable';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import {ExperimentFlowchart, ExperimentMethod} from 'studies/typed/shared/ExperimentalInputs';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT, NEAR_BLACK} from 'ui-library/Colors';

export interface ProcedureRunPlanProps {
  methods: Array<ExperimentMethod>;
  procedureFlowchart: ExperimentFlowchart;
  selectedMethodId: string;
  onChangeSelectedMethod: (newMethodId: string) => void;
  procedureType: ProcedureType;
}

const PLAN_BODY_PADDING = 24;

export const ExperimentalInputsContext = makeReactContext<ExperimentalInputsContextProperties>(() => {
  assert(false, 'This context cannot be skipped');
}, 'ExperimentalInputsContext');

export default function ProcedureRunPlan(props: ProcedureRunPlanProps): JSX.Element {
  const context = ExperimentalInputsContext.useContext();
  const selectedMethod = props.methods.find((m) => m.id === props.selectedMethodId);
  assert(selectedMethod != null, 'Selected method should not be null');
  const options = {context, selectedMethod, locked: true, procedureType: props.procedureType};
  const parameterTable = useInputTable(ExperimentalInputsComponentName.PARAMETERS, options);
  const materialInputsTable = useInputTable(ExperimentalInputsComponentName.MATERIAL_INPUTS, options);
  const equipmentTable = useInputTable(ExperimentalInputsComponentName.EQUIPMENT, options);
  const conditionsText = getProcedureRunConditionTextForProcedureType(props.procedureType, {plural: true});

  const shouldRenderAnyTable = parameterTable || materialInputsTable || equipmentTable;

  const value = useMemo(
    () => ({
      procedureValidationErrors: Object.entries(context.evaluatedValues).flatMap(([methodId, values]) =>
        values.hasBlockingValueErrors
          ? [
              {
                errorMessage: `Some ${conditionsText} have invalid values specified`,
                type: ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE,
                primaryObject: {id: methodId, objectType: ProcedureValidationObjectType.METHOD},
                parentObjects: [],
              } satisfies ProcedureValidationError,
            ]
          : []
      ),
      anyTabValidationErrors: false,
      setAnyTabValidationErrors: noop,
    }),
    [conditionsText, context.evaluatedValues]
  );

  return (
    <ProcedureValidationContext.Provider value={value}>
      <div
        className={classNames('u-flex-display mediocre', css(styles.experimentPlan))}
        {...getTestProps({component: 'ProcedureRunPlan', element: 'root'})}
      >
        <div className={css(styles.sidebarWrapper)}>
          <div className={css(styles.methodSelectorSidebar)}>
            <MethodSelectorSidebar
              methods={props.methods}
              methodIdKey="id"
              selectedMethodId={props.selectedMethodId}
              onChangeSelection={props.onChangeSelectedMethod}
              sidebarWidth={172}
              procedureType={props.procedureType}
            />
          </div>
        </div>
        <div className={classNames('scrollbar-override', css(styles.experimentPlanBody))}>
          <div className={css(styles.methodTitle)}>{selectedMethod.name}</div>
          {shouldRenderAnyTable ? (
            <>
              {materialInputsTable}
              {parameterTable}
              {equipmentTable}
            </>
          ) : (
            <div className={css(styles.noInputsBanner)}>
              <InlineBanner
                bannerType={InlineBannerType.WARNING}
                text={`Parameters, materials, and equipment were not added to this ${getProcedureUIString(
                  ProceduresStringKeys.METHOD,
                  // Hard-coded values OK because this component will be removed
                  props.procedureType,
                  {stringCase: StringCase.LOWER}
                )}.`}
                supportingInfo={`To vary ${conditionsText} for this ${getProcedureUIString(
                  ProceduresStringKeys.METHOD,
                  props.procedureType,
                  {stringCase: StringCase.LOWER}
                )}, close the experiment plan and edit this ${getProcedureUIString(
                  ProceduresStringKeys.METHOD,
                  props.procedureType,
                  {stringCase: StringCase.LOWER}
                )} in the ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, props.procedureType, {
                  stringCase: StringCase.LOWER,
                })}.`}
              />
            </div>
          )}
        </div>
      </div>
    </ProcedureValidationContext.Provider>
  );
}

const styles = StyleSheet.create({
  sidebarWrapper: {height: '100%', borderRight: `1px solid ${GRAY_LIGHT}`},
  methodSelectorSidebar: {marginRight: '-1px'},
  experimentPlan: {display: 'flex', flexDirection: 'row', height: '100%'},
  experimentPlanBody: {width: '100%', padding: PLAN_BODY_PADDING, overflowX: 'hidden', overflowY: 'auto'},
  methodTitle: {
    color: NEAR_BLACK,
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
    marginBottom: '2px',
    marginTop: '-12px',
    // TableEditable has 10px of padding, so add 10px to method title so they align
    paddingLeft: '10px',
  },
  noInputsBanner: {width: '50%'},
});
